/**
 * InvoiceViewerOverlay
 *
 * Full-screen overlay that renders a read-only, print-ready view of a raised
 * invoice using the existing InvoicePreview component (the same renderer used
 * in the Invoice Template Builder).
 *
 * Feature 2 — View Invoice as PDF (PRD: BILLING_PHASE2_PRD.md)
 *
 * Mock data strategy
 * ──────────────────
 * Until the backend exposes a GET /billing/invoices/:id/preview endpoint that
 * returns structured preview data (company info, client info, line items, etc.),
 * this component builds InvoicePreviewData from:
 *   1. The invoice row data already available in the table (number, date, due,
 *      description, amount, status).
 *   2. Hard-coded realistic mock values for everything else (company details,
 *      client contact, line items, totals).
 *
 * TODO (Engineering — Feature 2 backend integration):
 *   Replace `buildMockPreviewData()` with a real API call:
 *     GET /billing/invoices/:invoiceId/preview
 *   Response shape should match `InvoicePreviewData` from invoice-template-types.ts.
 *   Also replace `mockTemplates[0]` with the project's assigned template fetched from:
 *     GET /billing/projects/:projectId/template  (or from project settings).
 */

import React, { useEffect, useCallback, useState } from "react";
import { X, Download, CreditCard, Printer, RefreshCw, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { InvoicePreview } from "@/components/billing/InvoicePreview";
import { mockTemplates, mockPreviewData } from "@/lib/mock-invoice-templates";
import { getInvoiceTemplate } from "@/services/invoiceTemplateService";
import type { InvoicePreviewData, InvoiceTemplate } from "@/lib/invoice-template-types";
import { ResendInvoiceDialog } from "@/components/billing/ResendInvoiceDialog";
import { getStoredInvoicePdf, downloadPdf } from "@/services/invoice-pdf-service";
import type { AuditEvent } from "@/components/blocks/audit/project-audit-tab";

// ============================================================================
// Types
// ============================================================================

export type InvoiceViewerStatus =
  | "Sent"
  | "Overdue"
  | "PartiallyPaid"
  | "Paid"
  | "Credited"
  | "Void"
  | "CreditNote"
  | "WriteOff";

export interface InvoiceViewerInvoice {
  id: number;
  invoiceNumber: string | null;
  date: string | null;
  due: string | null;
  description: string;
  amount: number;
  status: InvoiceViewerStatus;
}

interface InvoiceViewerOverlayProps {
  invoice: InvoiceViewerInvoice | null;
  projectName?: string;
  clientName?: string;
  /**
   * Client billing email — pre-fills the "To" field in the ResendInvoiceDialog.
   * TODO (Engineering): Fetch from GET /projects/:id/client or pass from the
   * invoice row when the backend includes it.
   */
  clientEmail?: string;
  /** Client phone number for merge field resolution */
  clientPhone?: string;
  /** Client account number / code for merge field resolution */
  clientAccountNumber?: string;
  /** Client payment terms in days for merge field resolution */
  clientPaymentTermsDays?: string;
  /** Project code for merge field resolution */
  projectCode?: string;
  /** Contact first name for merge field resolution */
  contactFirstName?: string;
  /** Sender email for merge field resolution */
  senderEmail?: string;
  /**
   * The template ID saved in the project's billing settings.
   * The overlay fetches the full template object and passes it to InvoicePreview
   * so the rendered invoice matches exactly what the PM configured.
   * Falls back to mockTemplates[0] only if the fetch fails or no ID is provided.
   */
  templateId?: string | number | null;
  /**
   * Called when the user successfully sends or resends an invoice from within
   * this overlay. The parent (billing-tab / FixedFeeBillingTab) pushes the
   * event into the project-level audit log so it appears on the Audit tab.
   *
   * TODO (Engineering): Remove once send events are emitted server-side and
   * the Audit tab fetches them from GET /api/projects/:id/audit.
   */
  onAuditEntry?: (event: AuditEvent) => void;
  onClose: () => void;
}

// ============================================================================
// Mock preview data builder
// ============================================================================

/**
 * Builds InvoicePreviewData suitable for InvoicePreview from the invoice row
 * data available in the table plus realistic mock values for the rest.
 *
 * TODO (Engineering): Replace this entire function with a real API fetch:
 *   const previewData = await fetch(`/billing/invoices/${invoice.id}/preview`, {
 *     credentials: 'include',
 *   }).then(r => r.json());
 */
function buildMockPreviewData(
  invoice: InvoiceViewerInvoice,
  projectName: string,
  clientName: string,
): InvoicePreviewData {
  // Derive totals from the gross amount on the invoice row.
  // VAT is back-calculated at 20% standard rate.
  // TODO (Engineering): Use real subtotal / taxAmount / grossTotal from the API.
  const grossTotal = invoice.amount;
  const subtotal = Math.round((grossTotal / 1.2) * 100) / 100;
  const taxAmount = Math.round((grossTotal - subtotal) * 100) / 100;

  // Format a date string (yyyy-mm-dd) into dd/mm/yyyy for display.
  const fmtDate = (d: string | null) =>
    d
      ? d.split("-").reverse().join("/")
      : "—";

  return {
    // TODO (Engineering): Replace company block with real org/company data from settings API.
    company: {
      ...mockPreviewData.company,
    },
    invoice: {
      number: invoice.invoiceNumber ?? "DRAFT",
      date: fmtDate(invoice.date),
      dueDate: fmtDate(invoice.due),
      poNumber: undefined,
    },
    // TODO (Engineering): Replace client block with real client data from project/client API.
    client: {
      name: clientName,
      address: "456 Oxford Street\nManchester, M1 1AA\nUnited Kingdom",
      contact: "Accounts Payable",
    },
    project: {
      name: projectName,
      description: invoice.description,
    },
    billingPeriod: {
      start: fmtDate(invoice.date),
      end: fmtDate(invoice.due),
    },
    // TODO (Engineering): Replace line items with real invoice line items from
    //   GET /billing/invoices/:invoiceId/line-items
    //   Each item should map to the InvoicePreviewData.lineItems shape defined
    //   in invoice-template-types.ts.
    lineItems: mockPreviewData.lineItems.map((item, idx) => ({
      ...item,
      // Scale mock amounts proportionally so the totals match the real invoice amount.
      net: Math.round((subtotal / mockPreviewData.totals.subtotal) * item.net * 100) / 100,
      vat: Math.round((taxAmount / mockPreviewData.totals.tax) * item.vat * 100) / 100,
      amount:
        Math.round(
          ((subtotal / mockPreviewData.totals.subtotal) * item.net +
            (taxAmount / mockPreviewData.totals.tax) * item.vat) *
            100,
        ) / 100,
      id: String(idx + 1),
    })),
    totals: {
      subtotal,
      tax: taxAmount,
      discount: 0,
      total: grossTotal,
    },
  };
}

// ============================================================================
// Status badge helper
// ============================================================================

function StatusBadge({ status }: { status: InvoiceViewerStatus }) {
  const map: Record<InvoiceViewerStatus, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    Sent: { label: "Sent", variant: "default" },
    Overdue: { label: "Overdue", variant: "destructive" },
    PartiallyPaid: { label: "Partially Paid", variant: "secondary" },
    Paid: { label: "Paid", variant: "secondary" },
    Credited: { label: "Credited", variant: "outline" },
    Void: { label: "Void", variant: "outline" },
    CreditNote: { label: "Credit Note", variant: "outline" },
    WriteOff: { label: "Write Off", variant: "outline" },
  };
  const cfg = map[status] ?? { label: status, variant: "secondary" as const };
  return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
}

// ============================================================================
// InvoiceViewerOverlay
// ============================================================================

export function InvoiceViewerOverlay({
  invoice,
  projectName = "Project",
  clientName = "Client",
  clientEmail,
  clientPhone,
  clientAccountNumber,
  clientPaymentTermsDays,
  projectCode,
  contactFirstName,
  senderEmail,
  templateId,
  onAuditEntry,
  onClose,
}: InvoiceViewerOverlayProps) {
  // ── Resolved template state ──────────────────────────────────────────────
  // We start with the first mock template as an instant fallback so the
  // overlay renders immediately while the real template loads in the background.
  // Once getInvoiceTemplate() resolves we swap it in — the user sees the correct
  // branding/colours with no loading spinner.
  const [template, setTemplate] = useState<InvoiceTemplate>(mockTemplates[0]);

  // ── Feature 7: Resend Invoice state ────────────────────────────────────
  // Controls whether the ResendInvoiceDialog is visible.
  // Send/resend audit events are pushed to the Audit tab via onAuditEntry —
  // they do NOT live in this overlay's local state.
  const [resendDialogOpen, setResendDialogOpen] = useState(false);

  useEffect(() => {
    if (!templateId) return; // no ID → keep mock fallback
    getInvoiceTemplate(templateId)
      .then((tpl) => {
        if (tpl) {
          // Real template fetched — swap it in so InvoicePreview renders
          // exactly what the PM configured (colours, layout, columns, footer).
          setTemplate(tpl);
        }
        // If undefined (not found), keep the mock fallback silently.
      })
      .catch(() => {
        // Network/auth error — keep the mock fallback, don't crash the overlay.
        console.warn("[InvoiceViewerOverlay] Could not load project template; using default.");
      });
  }, [templateId]);

  // Close on Escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    // Prevent background scroll while overlay is open
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [handleKeyDown]);

  if (!invoice) return null;

  const previewData = buildMockPreviewData(invoice, projectName, clientName);

  // Shared "coming soon" toast for unimplemented actions
  const comingSoon = (label: string) => {
    toast.info(`${label} — coming soon`, {
      description: "This action is pending backend integration.",
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const showResend = ["Sent", "Overdue", "PartiallyPaid"].includes(invoice.status);
  const showCreditNote = ["Sent", "Overdue", "PartiallyPaid", "Paid"].includes(invoice.status);
  const showDownload = !["Void"].includes(invoice.status);

  return (
    <>
      {/* ── Print styles ────────────────────────────────────────────────── */}
      {/* Hides the overlay chrome so only the A4 invoice page prints.
          TODO (Engineering): Remove @media print block once server-side PDF
          generation is integrated (Feature 2 — PDF download endpoint). */}
      <style>{`
        @media print {
          body > *:not(#invoice-viewer-overlay) { display: none !important; }
          #invoice-viewer-overlay .overlay-chrome { display: none !important; }
          #invoice-viewer-overlay .overlay-scroll { overflow: visible !important; height: auto !important; }
          #invoice-viewer-overlay { position: static !important; }
        }
      `}</style>

      {/* ── Backdrop ────────────────────────────────────────────────────── */}
      <div
        id="invoice-viewer-overlay"
        className="fixed inset-0 z-50 flex flex-col bg-background"
        role="dialog"
        aria-modal="true"
        aria-label={`View invoice ${invoice.invoiceNumber ?? ""}`}
      >
        {/* ── Header bar ────────────────────────────────────────────────── */}
        <div className="overlay-chrome flex items-center justify-between gap-4 border-b bg-background px-6 py-3 shadow-sm shrink-0">
          {/* Left: invoice identity */}
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0 shrink-0"
              aria-label="Close invoice viewer"
            >
              <X className="h-4 w-4" />
            </Button>

            <div className="h-5 w-px bg-border shrink-0" />

            <div className="flex flex-col min-w-0">
              <span className="font-semibold text-sm truncate">
                {invoice.invoiceNumber ?? "Invoice"}
              </span>
              <span className="text-xs text-muted-foreground truncate">
                {projectName} · {clientName}
              </span>
            </div>

            <StatusBadge status={invoice.status} />
          </div>

          {/* Right: action buttons */}
          <div className="overlay-chrome flex items-center gap-2 shrink-0">
            {/* Print — triggers browser print-to-PDF until server-side PDF is ready.
                TODO (Engineering): Replace with a download from the document
                management store once GET /billing/invoices/:id/pdf is available. */}
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              className="gap-1.5"
            >
              <Printer className="h-4 w-4" />
              <span className="hidden sm:inline">Print</span>
            </Button>

            {showDownload && (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  const stored = await getStoredInvoicePdf(String(invoice.id));
                  if (stored) {
                    downloadPdf(stored.pdfBlob, `${invoice.invoiceNumber ?? `Invoice-${invoice.id}`}.pdf`);
                  } else {
                    toast.info("No PDF available", { description: "A PDF has not been generated for this invoice yet." });
                  }
                }}
                className="gap-1.5"
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">Download PDF</span>
              </Button>
            )}

            {/* Resend — Sent / Overdue / PartiallyPaid.
                Opens ResendInvoiceDialog (Feature 7). */}
            {showResend && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setResendDialogOpen(true)}
                className="gap-1.5"
              >
                <RefreshCw className="h-4 w-4" />
                <span className="hidden sm:inline">Resend</span>
              </Button>
            )}

            {/* Raise Credit Note — Sent / Overdue / PartiallyPaid / Paid only.
                TODO (Engineering): Open credit note creation flow (Feature 3). */}
            {showCreditNote && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => comingSoon("Raise Credit Note")}
                className="gap-1.5"
              >
                <CreditCard className="h-4 w-4" />
                <span className="hidden sm:inline">Raise Credit Note</span>
              </Button>
            )}

            {/* Close */}
            <Button variant="default" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>

        {/* ── Scrollable invoice canvas ────────────────────────────────── */}
        <div className="overlay-scroll flex-1 overflow-y-auto bg-muted/40 p-8">
          {/* The InvoicePreview component renders an A4-sized white paper div.
              We centre it inside the grey canvas area. */}
          <div className="flex justify-center">
            {/* MOCK DATA NOTICE — visible only in demo/dev mode.
                TODO (Engineering): Remove this banner once real preview data is
                wired up from the backend. */}
            <div className="w-full max-w-[900px] space-y-4">
              <div className="rounded-md border border-warning-200 bg-warning-50 px-4 py-2 text-xs text-warning-800 dark:border-warning-800 dark:bg-warning-950/30 dark:text-warning-400">
                <span className="font-semibold">Demo preview</span> — Invoice layout uses realistic mock line items. Real line items will be loaded from the backend once the preview API endpoint is ready.
              </div>

              <InvoicePreview template={template} previewData={previewData} />

              {/* Send History is shown in the project Audit tab, not here.
                  See: pages/project-details/tabs/audit-tab.tsx */}
            </div>
          </div>
        </div>
      </div>

      {/* ── ResendInvoiceDialog (Feature 7) ───────────────────────────────── */}
      {/* Rendered outside the overlay chrome so it overlays correctly.
          TODO (Engineering): Wire invoiceId to the real invoice ID from the
          data model so POST /api/invoices/:id/resend can be called. */}
      {showResend && (
        <ResendInvoiceDialog
          open={resendDialogOpen}
          onOpenChange={setResendDialogOpen}
          invoiceNumber={invoice.invoiceNumber ?? "Invoice"}
          invoiceId={invoice.id}
          clientEmail={clientEmail ?? ''}
          contactFirstName={contactFirstName ?? ''}
          clientName={clientName}
          clientPhone={clientPhone ?? ''}
          clientAccountNumber={clientAccountNumber ?? ''}
          clientPaymentTermsDays={clientPaymentTermsDays ?? ''}
          projectName={projectName}
          projectCode={projectCode ?? ''}
          invoiceTotal={`£${invoice.amount.toLocaleString("en-GB", { minimumFractionDigits: 2 })}`}
          dueDate={invoice.due ?? ""}
          senderEmail={senderEmail ?? ''}
          onResent={(entry) => {
            // Push a new AuditEvent to the project-level Audit tab.
            // TODO (Engineering): Remove this once server emits audit events
            // and the Audit tab fetches them from the real API.
            onAuditEntry?.({
              id: entry.id,
              category: "invoice",
              changedAt: entry.sentAt,
              changedBy: entry.sentByName,
              description: `Invoice ${invoice.invoiceNumber ?? "Invoice"} resent to ${entry.toEmail.join(", ")}${entry.ccEmail?.length ? ` (CC: ${entry.ccEmail.join(", ")})` : ""} — Subject: "${entry.subject}"`,
              metadata: {
                invoiceId: invoice.id,
                invoiceNumber: invoice.invoiceNumber,
                toEmail: entry.toEmail,
                ccEmail: entry.ccEmail,
                bccEmail: entry.bccEmail,
                subject: entry.subject,
                isResend: true,
              },
            });
          }}
        />
      )}
    </>
  );
}
